'use strict';
import assign from 'lodash/assign';
import isArray from 'lodash/isArray';
import * as T from 'prop-types';
import React, {Component} from 'react';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/retryWhen';
import 'rxjs/add/operator/sampleTime';
import 'rxjs/add/operator/share';
import 'rxjs/add/operator/skip';
import 'rxjs/add/operator/startWith';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import {getApiToken} from '../../../auth/reducers';
import {API_URL} from '../../../config/constants';
import {ViewMode} from '../../../core-utilities/enums/ViewMode';
import taskScheduleModalTemplate from '../../../tasks/controllers/task-schedule-modal.html';
import {getPermissions, TaskScheduleDialogController} from '../../../tasks/controllers/TaskScheduleDialogController';
import {
  assignedToEquals,
  combineParams,
  convertResponse,
  convertToEvent,
  floorMonth,
  getAssignedTo,
  getDateRange,
  getErrorMode,
  getResponse,
  rangesAreEqual,
  returnSelf,
} from '../../utils';
import {CalendarView, OrgCalendarView} from './OrgCalendarView';


const TASK_SCHEDULE_UPDATED_INDEX = 'taskScheduleUpdated';

// Calendar Configuration

const getErrorState = () => ({mode: getErrorMode(), data: []});

// TODO: configure translation
export class OrgCalendarComponent extends Component {
  static propTypes = {
    orgId: T.string.isRequired,
  };

  static $inject = [
    '$ngRedux',
    '$modal',
    '$permissions',
    'fetchUtils',
    'FirebaseUtil',
    'AssetTaskDomain',
    'TaskDomain',
  ];

  constructor(props) {
    super(props);
    /**
     * @property {ViewMode} state.mode
     * @property {Array} state.data
     */
    this.state = {
      mode: ViewMode.LOADING,
      data: null,
      currentView: CalendarView.month,
    };

    this.handleNavigate = this.handleNavigate.bind(this);
    this.handleView = this.handleView.bind(this);
    this.handleSelectEvent = this.handleSelectEvent.bind(this);
  }

  componentDidMount() {
    // we cannot pass in props until we've subscribed to the stream.
    // BehaviorSubject might be the wrong choice here
    this.props$ = new BehaviorSubject({});
    this.date$ = new BehaviorSubject(new Date());
    const data$ = Observable.combineLatest(
      this.date$
        .map(getDateRange)
        .distinctUntilChanged(rangesAreEqual),
      this.props$
        .distinctUntilChanged(OrgCalendarComponent.propsAreEqual),
      combineParams
    ).share(); // hot observable
    const update$ = data$.switchMap(OrgCalendarComponent.createLastUpdatedObservable);

    this.subscription = Observable.combineLatest(
      data$,
      update$,
      (data, update) => {
        // first request for a new view. subsequent events have a timestamp
        // also, clear the error if the previous attempt failed
        if (update === null || this.state.mode !== ViewMode.SUCCESS) {
          this.setState({
            mode: ViewMode.LOADING,
          });
        }
        return data;
      })
      .switchMap(this.createRequestObservable.bind(this))
      .delay(0)
      .subscribe(data => {
        this.setState(data === null || isArray(data) ? {
          mode: ViewMode.SUCCESS,
          data: data ? data : [],
        } : getErrorState() /* this shouldn't happen, but just in case, treat it as an error */);
      }, error => {
        // TODO: this should not happen and we should probably treat it as an error and resubscribe to the stream
        console.warn('on error called', error);
        this.setState(getErrorState());
      }, () => {
        // TODO: this should not happen and we should probably treat it as an error and resubscribe to the stream
        console.warn('on complete called');
      });

    // publish initial value after observables are wired up
    if (this.props.orgId)
      this.props$.next(this.props);
  }

  createRequestObservable(data) {
    return Observable.fromPromise(OrgCalendarComponent.requestCalendarData(data))
      .retryWhen(errors =>
        Observable.range(1, 4)
          .zip(errors, returnSelf)
          .mergeMap(i => {
            if (i < 4) {
              return Observable.timer(i * 1000);
            } else {
              this.setState(getErrorState());
              return errors;
            }
          })
      );
  }

  componentWillUnmount() {
    this.props$ = null;
    // cleanup calendar data observable
    this.date$.unsubscribe();
    this.subscription.unsubscribe();
  }

  componentWillReceiveProps(nextProps) {
    if (!OrgCalendarComponent.propsAreEqual(this.props, nextProps)) {
      setTimeout(() => this.props$ && this.props$.next(nextProps));
    }
  }

  handleNavigate(date, view) {
    this.date$.next(date);
  }

  handleView(view) {
    this.setState({
      currentView: view,
    });
  }

  handleSelectEvent(event) {
    const stateData = this.state.data;
    const originalSchedule = event.task.scheduled || null;
    const originalSubmittedOn = event.task.submittedOn || null;
    const originalUseTime = event.task.useTime || false;
    const originalDuration = event.task.duration || null;
    const originalAssignedTo = event.task.assignedTo || null;
    return Promise
      .all([
        getPermissions(OrgCalendarComponent.dependencies.$permissions, event.task),
        OrgCalendarComponent.getTaskInstance(event.task),
      ])
      .then(data =>
        OrgCalendarComponent.dependencies.$modal.show({
          template: taskScheduleModalTemplate,
          controller: TaskScheduleDialogController,
          locals: {task: data[1], permissions: data[0], mode: 'foreign'},
        })
      )
      .then(updatedTask => {
        if (!updatedTask) return; // this shouldn't happen
        // make sure the state has not been changed since the event was selected
        if (stateData !== this.state.data) return;
        const newScheduled = updatedTask.scheduled ? updatedTask.scheduled.getTime() : null;
        const newSubmittedOn = updatedTask.submittedOn ? updatedTask.submittedOn.getTime() : null;
        const newUseTime = updatedTask.useTime || false;
        const newDuration = updatedTask.duration || null;
        const newAssignedTo = updatedTask.assignedTo || null;
        if (newScheduled !== originalSchedule
          || newSubmittedOn !== originalSubmittedOn
          || newUseTime !== originalUseTime
          || newDuration !== originalDuration
          || !assignedToEquals(newAssignedTo, originalAssignedTo)) {
          const result = OrgCalendarComponent.updateEventSchedule(
            stateData,
            event,
            newScheduled,
            newSubmittedOn,
            newUseTime,
            newDuration,
            newAssignedTo,
          );
          if (isArray(result)) {
            this.setState({
              data: result,
            });
          }
        }
      });
  }

  render() {
    const now = new Date();
    return (
      <OrgCalendarView
        currentView={this.state.currentView}
        events={this.state.data ? this.state.data : []}
        mode={this.state.mode}
        defaultDate={new Date(now.getFullYear(), now.getMonth(), 1)}
        breadcrumbPath={this.props.breadcrumbPath}

        onNavigate={this.handleNavigate}
        onView={this.handleView}
        onSelectEvent={this.handleSelectEvent}

        eventPropGetter={OrgCalendarComponent.eventPropGetter}
      />
    );
  }

  static eventPropGetter(event) {
    if (event && event.task) {
      return {
        className: OrgCalendarComponent.dependencies.TaskDomain.getScheduleStatus(event.task),
      };
    }
  }

  static propsAreEqual(a, b) {
    if (!a && !b) {
      return true;
    } else if (!a || !b) {
      return false;
    } else {
      return a.orgId === b.orgId;
    }
  }

  static requestCalendarData(data) {
    const token = getApiToken(OrgCalendarComponent.dependencies.$ngRedux.getState());
    const request = {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache', // may need to disable this in production
        'Authorization': 'Bearer ' + token,
      },
      mode: 'cors',
      cache: 'default',
    };
    // TODO: make 'organizations' an input so this component can be reused for projects and asset-projects calendars
    const url = [
      API_URL,
      '/api/calendar/organizations/',
      data.orgId,
      '?timestart=',
      data.start.getTime(),
      '&timeend=',
      data.end.getTime(),
    ].join('');

    return OrgCalendarComponent.dependencies.fetchUtils.rawRequest(url, request)
      .then((res) => res.data)
      .then(convertResponse);
  }

  static createLastUpdatedObservable(data) {
    const start = data.start.getTime();
    // gracefully handle missing ID
    if (!data.orgId)
      return Observable.of(null);
    return OrgCalendarComponent.dependencies.FirebaseUtil.firebaseRef()
      .child(TASK_SCHEDULE_UPDATED_INDEX)
      .child(data.orgId)
      .child('' + floorMonth(start + (data.end.getTime() - start) / 2))
      .observe('value')
      .map(ss => ss.val())
      .skip(1) // always ignore the first value since it will very nearly always match the initial request
      .sampleTime(5000)
      .startWith(null)
      .retryWhen(errors =>
        Observable.range(1, 4)
          .zip(errors, returnSelf)
          .mergeMap(i => i < 4 ? Observable.timer(i * 5000) : errors)
      );
  }

  static getTaskInstance(task) {
    if (task.collection.indexOf('asset') > -1) {
      return OrgCalendarComponent.dependencies.AssetTaskDomain.$get(task.id);
    } else {
      return OrgCalendarComponent.dependencies.TaskDomain.$get(task.id);
    }
  }

  static updateEventSchedule(
    stateData,
    targetEvent,
    newScheduled,
    newSubmittedOn,
    newUseTime,
    newDuration,
    newAssignedTo,
  ) {
    let changedTask = false;
    stateData.forEach((event, idx, array) => {
      if (event.task.id === targetEvent.task.id && (event.task.scheduled !== newScheduled
        || event.task.submittedOn !== newSubmittedOn
        || event.task.useTime !== newUseTime
        || event.task.duration !== newDuration
        || !assignedToEquals(event.task.assignedTo, newAssignedTo))) {
        const newTask = assign({}, event.task, {
          scheduled: newScheduled,
          submittedOn: newSubmittedOn,
          useTime: newUseTime,
          duration: newDuration,
          assignedTo: getAssignedTo(newAssignedTo),
        });
        array[idx] = convertToEvent(newTask);
        changedTask = true;
      }
    });

    if (changedTask) {
      return stateData;
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/calendar/components/OrgCalendar/OrgCalendarComponent.js